import React, { useRef, createContext, useContext, useState, useCallback } from "react";
import { Eraser } from "lucide-react";

/* 员工入职自助门户共用：公开 API（token）/ 表单控件 / 签名板 / 步骤元数据 / 显示语言（默认纯英文，可切换「英文 + 中文对照」） */
export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const inp = "mt-1 w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400/50 bg-white";
export const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];

export const STEP_META = [
  { k: "personal", en: "Personal Info", zh: "个人信息" },
  { k: "address", en: "Address", zh: "地址" },
  { k: "emergency", en: "Emergency Contacts", zh: "紧急联系人" },
  { k: "deposit", en: "Direct Deposit", zh: "直接存款" },
  { k: "pay", en: "Pay Schedule", zh: "薪资周期" },
  { k: "w4", en: "Federal W-4", zh: "联邦 W-4" },
  { k: "de4", en: "California DE-4", zh: "加州 DE-4" },
  { k: "i9", en: "Form I-9 · Section 1", zh: "I-9 第 1 部分" },
  { k: "eeo", en: "Voluntary EEO", zh: "自愿 EEO" },
  { k: "wotc", en: "WOTC Pre-screen", zh: "WOTC 预筛" },
  { k: "review", en: "Review & Sign", zh: "复核并签署" },
];

/* ---- 显示语言：en = 纯英文（默认）；bi = 英文 + 中文对照 ---- */
const LANG_KEY = "onb_lang";
const LangCtx = createContext({ mode: "en", setMode: () => {} });
export function OnbLangProvider({ children }) {
  const [mode, setModeState] = useState(() => (typeof window !== "undefined" && window.localStorage.getItem(LANG_KEY) === "bi" ? "bi" : "en"));
  const setMode = useCallback((m) => { setModeState(m); try { window.localStorage.setItem(LANG_KEY, m); } catch { /* ignore */ } }, []);
  return <LangCtx.Provider value={{ mode, setMode }}>{children}</LangCtx.Provider>;
}
export const useLang = () => useContext(LangCtx);
/* L(en, zh)：纯英文模式只显示英文；对照模式英文后附灰色中文。Ls 为字符串版（placeholder / title 等属性用） */
export function useL() {
  const { mode } = useLang();
  const L = (en, zh) => (mode === "bi" && zh ? <>{en} <span className="text-slate-400 font-normal">{zh}</span></> : en);
  const Ls = (en, zh) => (mode === "bi" && zh ? `${en} / ${zh}` : en);
  return { L, Ls, bi: mode === "bi" };
}

async function _req(method, path, body, isForm) {
  const res = await fetch(`${BACKEND}/api/public/onboarding/${path}`, { method, headers: isForm ? undefined : { "Content-Type": "application/json" }, body: isForm ? body : body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}
export const pub = {
  get: (token) => _req("GET", token),
  step: (token, step, body) => _req("PUT", `${token}/step/${step}`, body),
  submit: (token, body) => _req("POST", `${token}/submit`, body),
  upload: (token, fd) => _req("POST", `${token}/i9-doc`, fd, true),
  delDoc: (token, did) => _req("DELETE", `${token}/i9-doc/${did}`),
  docUrl: (token, did) => `${BACKEND}/api/public/onboarding/${token}/i9-doc/${did}`,
};

export function Field({ label, zh, children, hint, hintZh, className = "", required }) {
  const { bi } = useL();
  return (
    <label className={`block text-[13px] text-slate-700 ${className}`}>
      <span className="font-semibold">{label}{required && <span className="text-red-500"> *</span>}</span>{bi && zh && <span className="text-slate-400 font-normal ml-1.5 text-[12px]">{zh}</span>}
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-0.5">{hint}{bi && hintZh ? ` / ${hintZh}` : ""}</span>}
    </label>
  );
}

export function Check({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
      <input type="checkbox" data-testid={testid} className="mt-0.5 h-4 w-4 accent-red-600" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

export function Radio({ name, value, current, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
      <input type="radio" name={name} data-testid={testid} className="mt-0.5 h-4 w-4 accent-red-600" checked={current === value} onChange={() => onChange(value)} />
      <span>{children}</span>
    </label>
  );
}

export function SignaturePad({ onChange, testid = "onb-signature-pad" }) {
  const { L } = useL();
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} data-testid={testid}
        className="w-full h-36 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button type="button" onClick={clear} data-testid={`${testid}-clear`} className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />{L("Clear and sign again", "清除重签")}</button>
    </div>
  );
}

export const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
